<template>
  <div
    class="asc-report-details-wrapper--container"
    data-automation-id="wrapper_report_details"
  >
    <v-row no-gutters>
      <v-col cols="12" sm="12" md="12" lg="9">
        <div class="asc-report-details-wrapper--container-left">
          <asc-surface-title title="REPORT DATA" class="report-data-title" />
          <asc-spinner-wrapper :loading="loading">
            <asc-surface-support-text
              :text="getReportDateDataItem"
              class="asc-report-data-list--date-parsed"
            />
            <asc-link-button
              class="asc-report-data-list--view-raw-report"
              text="View Raw Report"
              @click="onShowRawReport"
            ></asc-link-button>
            <div
              v-if="isSwitchDisplayed"
              class="asc-report-data-list--switch-container"
            >
              <asc-switch
                v-model="consolidated"
                label="All Parameters"
                single-line
              ></asc-switch>
            </div>
            <asc-report-data-item
              v-for="item in getReportFilteredItems"
              :key="item.uniqueId"
              class="asc-report-data-list--margins"
              :report="item"
              :on-report-menu-click="onReportMenuClick"
            ></asc-report-data-item>
          </asc-spinner-wrapper>
          <asc-no-data v-if="isEmpty"></asc-no-data>
        </div>
      </v-col>
      <v-col cols="12" sm="12" md="12" lg="3">
        <div class="asc-report-details-wrapper--container-right">
          <template v-if="exportButtonVisible">
            <asc-report-export-wrapper
              title="Export"
              :loading="loading"
              :on-export-report="onExportReport"
            ></asc-report-export-wrapper>
          </template>
          <asc-report-alert-wrapper
            title="Alerts"
            :values="reportAlerts"
            :loading="loading"
            :on-report-alert-click="onReportAlertClick"
          ></asc-report-alert-wrapper>
          <asc-aircraft-details-wrapper
            title="Aircraft Details"
            :values="aircraftDetails"
            :loading="aircraftDetailsLoading"
          ></asc-aircraft-details-wrapper>
          <asc-fault-monitor-wrapper
            title="Fault Monitors"
            :values="faultMonitors"
            :loading="loading"
          ></asc-fault-monitor-wrapper>
        </div>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import AscLinkButton from '@/components/atoms/AscLinkButton'
import AscNoData from '@/components/atoms/AscNoData'
import AscSpinnerWrapper from '@/components/atoms/AscSpinnerWrapper'
import AscSurfaceSupportText from '@/components/atoms/AscSurfaceSupportText'
import AscSurfaceTitle from '@/components/atoms/AscSurfaceTitle'
import AscSwitch from '@/components/atoms/AscSwitch'
import { AscReportMenuClickMixin } from '@/components/mixins'
import AscReportDataItem from '@/components/molecules/AscReportDataItem'
import {
  AscAircraftDetailsWrapper,
  AscFaultMonitorWrapper,
  AscReportAlertWrapper,
} from '@/components/organisms'
import * as constants from '@/utilities/appConsts'
import AscReportExportWrapper from './AscReportExportWrapper.vue'

export default {
  name: 'AscReportDetailsWrapper',

  components: {
    AscNoData,
    AscReportDataItem,
    AscSpinnerWrapper,
    AscSwitch,
    AscReportAlertWrapper,
    AscAircraftDetailsWrapper,
    AscFaultMonitorWrapper,
    AscReportExportWrapper,
    AscLinkButton,
    AscSurfaceTitle,
    AscSurfaceSupportText,
  },

  mixins: [AscReportMenuClickMixin],

  props: {
    isApuReport: {
      type: Boolean,
      default: false,
    },
    items: {
      type: Array,
      required: true,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    aircraftDetailsLoading: {
      type: Boolean,
      default: false,
    },
    exportButtonVisible: {
      type: Boolean,
      default: false,
    },
    onExportReport: {
      type: Function,
      required: true,
    },
    reportAlerts: {
      type: Array,
      required: true,
    },
    onReportAlertClick: {
      type: Function,
      default: () => {},
    },
    aircraftDetails: {
      type: Array,
      required: true,
    },
    faultMonitors: {
      type: Array,
      required: true,
    },
    onShowRawReport: {
      type: Function,
      default: () => {},
    },
  },

  data() {
    return {
      consolidated: false,
    }
  },

  computed: {
    isEmpty() {
      return !this.loading && this.items.length === 0
    },

    isSwitchDisplayed() {
      return !this.isEmpty && this.isApuReport
    },

    getReportDetailItems() {
      return this.items.filter((item, index) => {
        return !(
          item.type === 'title' ||
          item.type === 'date' ||
          (index === 1 && item.type === 'columnTable')
        )
      })
    },

    getReportFilteredItems() {
      if (this.isApuReport) {
        return this.getReportDetailItems.filter(item => {
          if (!this.consolidated) {
            return (
              item.consolidated === constants.CONSOLIDATED_VALUES.c ||
              item.consolidated === constants.CONSOLIDATED_VALUES.b
            )
          }
          if (this.consolidated) {
            return (
              item.consolidated === constants.CONSOLIDATED_VALUES.a ||
              item.consolidated === constants.CONSOLIDATED_VALUES.b
            )
          }
          return true
        })
      }

      if (!this.isApuReport) {
        return this.getReportDetailItems
      }

      return false
    },

    getReportDateDataItem() {
      if (this.getReportInfoItems.length === 0) {
        return null
      }
      const [first] = this.getReportInfoItems.filter(
        item => item.type === 'date',
      )
      return first.text
    },

    getReportInfoItems() {
      return this.items.filter((item, index) => {
        return (
          item.type === 'title' ||
          item.type === 'date' ||
          (index === 1 && item.type === 'columnTable')
        )
      })
    },
  },
}
</script>

<style lang="scss" scoped>
@media (max-width: 1260px) {
  .asc-report-details-wrapper {
    margin-right: 0;
  }
}

.asc-report-details-wrapper--container-left {
  .report-data-title {
    margin-bottom: 20px;

    .asc-header--h3 {
      font-size: 14px;
      line-height: 18px;
      letter-spacing: 1px;
      font-weight: 500;
    }
  }

  .asc-report-data-list--view-raw-report {
    padding: 10px 0 20px 0 !important;
  }
}

.asc-report-data-list {
  &--switch-container {
    margin-top: 10px;
  }
}
</style>
